/**
 * Script-review AI response shapes — mirror the BE/AI service contract.
 * UI helpers (severity tones, recommendation parsing) stay in
 * `@discovr/ui/components/common/ScriptReview/types.ts` because they
 * embed Tailwind class strings.
 */

export type ScriptReviewTaskResponse = {
  task_id: string;
  status: 'PENDING' | 'SUCCESS' | 'FAILURE';
  result?: ScriptReviewResult;
  analysis_id?: string;
  result_summary?: string;
  error?: string;
};

export type ScriptReviewAiEnvelope = {
  status: 'pending' | 'complete' | 'failed';
  analysis_id?: string;
  data?: ScriptReviewResult;
  error?: string;
};

// ─── GEO optimizer pack (mirrors the AI service GeoPackResult) ───────────────

/** A source-language string with optional translations (e.g. {en: "..."}). */
export type GeoTranslated = { text: string; translations?: Record<string, string> };

export type GeoTitleOption = {
  text: GeoTranslated;
  recommended: boolean;
  target_questions: string[];
  why: string;
};
export type GeoQABlock = { question: string; answer: GeoTranslated; target_question: string };
export type GeoDescription = { body: GeoTranslated; qa_blocks: GeoQABlock[]; why: string };
export type GeoChapter = { timestamp: string; title: GeoTranslated; answers: string };
export type GeoPinnedComment = { body: GeoTranslated; why: string };
export type GeoOnScreenCue = { timestamp: string; text: GeoTranslated; reason: string };
export type GeoVoiceMatch = { used_profile: boolean; summary: string; handle?: string; traits?: string[]; source_videos: string[] };
export type GeoNeedsConfirmation = { claim: string; where: string; reason: string };
export type GeoAuthenticity = {
  passed: boolean;
  blocked: boolean;
  unsourced_count: number;
  disclosure_present: boolean;
  notes: string[];
};

export type GeoPack = {
  detected_language: string;
  target_questions: string[];
  titles: GeoTitleOption[];
  description: GeoDescription;
  chapters: GeoChapter[];
  pinned_comment: GeoPinnedComment;
  on_screen_cues: GeoOnScreenCue[];
  voice_match: GeoVoiceMatch;
  needs_confirmation?: GeoNeedsConfirmation[];
  authenticity?: GeoAuthenticity | null;
};

/** The Go proxy passes the AI service's GET body through: { analysis_id, result }. */
export type GeoPackWrapper = { result?: GeoPack; analysis_id?: string };

/** Envelope on ScriptDetail.geo_analysis — same shape as the script-review one. */
export type GeoAiEnvelope = {
  status: 'pending' | 'processing' | 'complete' | 'failed';
  analysis_id?: string;
  data?: GeoPackWrapper;
  error?: string;
  /** Persisted GEO per-element selection (element keys like "title:0",
   *  "description", "chapters", "pinned", "cues") — the approved/chosen set,
   *  auto-saved from the GEO Optimized tab so it survives reopening. */
  selected?: string[];
  /** Persisted CONTENT-review finalize selection (keys "chapters",
   *  "description") — separate from `selected`, auto-saved from the Content
   *  Review GEO tab so it survives reopening and shows in Confirmed. */
  finalize_selected?: string[];
};

export type Severity = 'pass' | 'minor' | 'major' | 'critical';
export type LineSeverity = 'minor' | 'major' | 'critical';

export type ScoreBreakdown = {
  score: number;
  reasoning: string;
  pillars: Array<{ name: string; score: number; evidence: string }>;
};

export type VariantMeta = {
  label: string;
  creative_direction: string;
  risk_profile: 'low' | 'medium' | 'high';
  key_changes: string[];
  when_to_use: string;
};

/**
 * The sentinel the backend emits on `platform_variants_meta.{key}.creative_direction`
 * when the model couldn't produce a genuinely distinct rewrite for a lane.
 */
export const VARIANT_INSUFFICIENT_RANGE_SENTINEL =
  'insufficient creative range — see safe variant';

export type TranslatedString = {
  text: string;
  translations?: Record<string, string>;
};

export type TranslatableField = TranslatedString | string;

export type ScriptReviewResult = {
  detected_language?: string;
  script_translations?: Record<string, string>;

  reasoning: string;
  status_recommendation: string;
  overall_score: number;

  brand_fit_score: number;
  compliance_score: number;
  quality_score: number;

  hook_pacing_cta_scores: {
    hook_strength: number;
    pacing: number;
    cta_clarity?: number;
  };

  feedback: string;
  strengths: string[];

  estimated_duration_seconds: number;

  human_in_loop: {
    requires_manual_review: boolean;
    reasons: string[];
  };

  line_by_line_feedback: Array<{
    original_text: TranslatableField;
    issue: string;
    suggestion: TranslatableField;
    aspect: 'hook' | 'clarity' | 'pacing' | 'cta' | 'compliance' | 'brand_safety' | 'other' | string;
    severity?: LineSeverity;
    confidence?: number;
    beat_label?: string | null;
  }>;

  platform_fit_edits: {
    youtube_longform: TranslatableField;
    reels: TranslatableField;
    shorts: TranslatableField;
  };

  platform_variants: {
    safe: TranslatableField;
    bolder: TranslatableField;
    shorter: TranslatableField;
  };

  brand_safety_issues: Array<{
    disallowed_claim_or_competitor?: TranslatableField;
    explanation?: string;
    recommended_fix?: TranslatableField;
    [key: string]: unknown;
  }>;

  automated_checks: Array<{
    title: string;
    passed: boolean;
    details: string;
    reasoning: string;
    recommendation: TranslatableField;
    severity?: Severity;
  }>;

  executive_summary?: {
    one_line_for_brand: string;
    one_line_for_creator: string;
    verdict_confidence: 'high' | 'medium' | 'low';
    estimated_approval_probability: number;
    top_wins: string[];
    top_risks: string[];
  };

  brand_fit_breakdown?: ScoreBreakdown;
  compliance_breakdown?: ScoreBreakdown;
  quality_breakdown?: ScoreBreakdown;

  hook_pacing_cta_detail?: {
    hook: {
      score: number;
      quote: string;
      pattern_type: 'curiosity_gap' | 'pattern_interrupt' | 'promise' | 'question' | 'other' | string;
      timing_seconds: number;
      reasoning: string;
    };
    pacing: {
      score: number;
      wpm_estimate: number;
      slow_segments: string[];
      reasoning: string;
    };
    cta: {
      score: number;
      quote: string;
      action_verb_present: boolean;
      urgency_present: boolean;
      reasoning: string;
    };
  };

  brief_coverage?: Array<{
    requirement_id: string;
    requirement_text: string;
    status: 'met' | 'partial' | 'missing';
    evidence: string;
  }>;

  brand_safety_audit?: {
    checked_competitors: string[];
    checked_disallowed_claims: string[];
    checked_disclaimers: string[];
    findings_summary: string;
  };

  platform_variants_meta?: {
    safe: VariantMeta;
    bolder: VariantMeta;
    shorter: VariantMeta;
  };
};

export type Recommendation = 'approved' | 'needs_revision' | 'rejected' | 'unknown';
export type Strength = 'strong' | 'fair' | 'weak';
